/**
 * Admin tool: publish + manage the software releases offered on /downloads.
 *
 * Self-contained (InviteUserDialog precedent): owns its queries via the
 * Downloads hooks. The /downloads page itself is read-only for users — all
 * publish/delete surfaces live here, server-enforced by require_xer_admin.
 */
import React from 'react';
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { toast } from 'sonner';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { pendingTargetId } from '../../../utils/pendingTargetId';
import {
  useDeleteReleaseMutation,
  useSoftwareReleasesQuery,
} from '../../Downloads/Downloads.queries';
import { PublishReleasePanel } from '../../Downloads/PublishReleasePanel';
import { formatBytes } from '../../Downloads/formatBytes';
import { resolveProductLabel, SoftwareRelease } from '../../../types/downloads';

/** Prefer the product row the API embedded on the release; fall back to the
 * bundled map for a release whose embed the server did not send (an older
 * bundle, or the window between a deploy and its migration apply). */
const labelFor = (release: SoftwareRelease): string =>
  resolveProductLabel(release.product, release.software_products?.label ?? null);

interface SoftwareReleasesDialogProps {
  open: boolean;
  onClose: () => void;
}

export const SoftwareReleasesDialog: React.FC<SoftwareReleasesDialogProps> = ({
  open,
  onClose,
}) => {
  // The dialog stays mounted with open=false on /admin-tools — fetch only
  // once it's actually opened (sibling AdminTools queries gate the same way).
  const releasesQuery = useSoftwareReleasesQuery(open);
  const deleteMutation = useDeleteReleaseMutation();
  const releases = releasesQuery.data ?? [];

  // Which ROW is being deleted (#1312). One mutation instance serves the whole
  // table, so `deleteMutation.isPending` is a property of the TABLE — wired
  // straight to every button it greys out the entire catalog while one delete
  // is in flight, and an admin clearing out three stale releases has to wait
  // out each round trip. `pendingTargetId` reads `isPending` BEFORE `variables`
  // for the reason in its docstring: react-query keeps the last `variables`
  // after settle, so the naive read leaves the just-deleted row's successor
  // disabled forever.
  const deletingReleaseId = pendingTargetId(deleteMutation.isPending, deleteMutation.variables);

  const handleDelete = async (release: SoftwareRelease) => {
    if (
      !window.confirm(
        `Delete ${labelFor(release)} ${
          release.version
        }? The installer is removed from S3; download history is kept.`
      )
    ) {
      return;
    }
    try {
      await deleteMutation.mutateAsync(release.id);
      toast.success(`Deleted ${labelFor(release)} ${release.version}`);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to delete release'));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="software-releases-dialog"
    >
      <DialogTitle>Software Releases</DialogTitle>
      <DialogContent>
        <PublishReleasePanel />

        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Published releases
        </Typography>
        {releasesQuery.isLoading && (
          <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
        )}
        {releasesQuery.isError && (
          <Alert severity="error">Failed to load releases. Try reloading the page.</Alert>
        )}
        {releasesQuery.isSuccess && releases.length === 0 && (
          <Alert severity="info">No software releases have been published yet.</Alert>
        )}
        {releases.length > 0 && (
          <TableContainer>
            <Table size="small" data-testid="admin-releases-table">
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Channel</TableCell>
                  <TableCell>Released</TableCell>
                  <TableCell align="right">Size</TableCell>
                  <TableCell>SHA256</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {releases.map((release) => (
                  <TableRow key={release.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {labelFor(release)}
                        {release.audience === 'xer_internal' && (
                          <Tooltip title="Visible to XER staff only — customers never see this product on /downloads">
                            <Chip size="small" variant="outlined" label="Internal" />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{release.version}</TableCell>
                    <TableCell>{release.channel}</TableCell>
                    <TableCell>{new Date(release.released_at).toLocaleDateString()}</TableCell>
                    <TableCell align="right">{formatBytes(release.size_bytes)}</TableCell>
                    <TableCell>
                      <Tooltip title={release.sha256}>
                        <Box component="span" sx={{ fontFamily: 'monospace' }}>
                          {release.sha256.slice(0, 12)}…
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Delete release">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => void handleDelete(release)}
                          disabled={deletingReleaseId === release.id}
                          data-testid="admin-release-delete-btn"
                          data-release-id={release.id}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
